/**
 * The one description of an org's email-send allowance that every limit
 * surface shares: the upgrade dialog, the dialog's fallback toast, the
 * "send limit" notification, the public API / MCP 402 and the agent's usage
 * summary. Admission is `limit − used − reserved`, so a send can be refused
 * while most of the allowance is unused: the rest is reserved by sends still
 * delivering. Saying "you've used all 50,000" then is false (2026-10-14: 911
 * were used and 27,880 reserved by a canceled send).
 *
 * Delivery turns reserved sends into used ones, one for one, so a send that
 * finishes frees nothing: only a cancel returns what a send has not sent yet.
 */

import { BILLING_PLANS, isBillingPlanKey } from './plans'

/**
 * What a refusal hands its caller (in-app 429 `details.quota`, API/MCP 402
 * `details`, audience-run results) so it can tell a used-up allowance from
 * one that sends in progress reserved.
 */
export type EmailSendQuotaDetails = {
  used: number
  reserved: number
  remaining: number
  limit: number
  planKey: string
  requested: number
  /** When the allowance resets (the billing period's end, epoch ms), if known. */
  periodEnd?: number
}

export type EmailSendQuotaNumbers = {
  /** Provider-accepted sends this billing period. */
  used: number
  /** Held by sends still delivering. */
  reserved: number
  limit: number
  /** The send that was refused; absent for a passive usage read. */
  requested?: number
}

/** How delivery and cancels move reserved sends, wherever they are named. */
export const RESERVED_SENDS_EXPLAINER =
  'Reserved sends count as used as they deliver; canceling a send returns what it has not sent yet.'

/**
 * - `exhausted`: every included send was used.
 * - `shortfall`: a send needs more than is left (used + reserved + requested
 *   would exceed the limit).
 * - `reserved_only`: nothing is left, but only because sends in progress
 *   reserved it.
 */
export type EmailSendQuotaKind =
  | 'exhausted'
  | 'shortfall'
  | 'reserved_only'
  | 'available'

/** The numbers every refusal payload carries (`EmailSendQuotaDetails`). */
export function toEmailSendQuotaDetails(quota: {
  used: number
  reserved?: number
  remaining?: number
  limit: number
  planKey: string
  requested: number
  periodEnd?: number
}): EmailSendQuotaDetails {
  const reserved = Math.max(quota.reserved ?? 0, 0)
  return {
    used: quota.used,
    reserved,
    remaining: quota.remaining ?? remainingEmailSends({ ...quota, reserved }),
    limit: quota.limit,
    planKey: quota.planKey,
    requested: quota.requested,
    ...(quota.periodEnd === undefined ? {} : { periodEnd: quota.periodEnd }),
  }
}

/** "Growth" for `growth`; nothing for a key no plan has. */
export function planNameOf(planKey: string): string | undefined {
  return isBillingPlanKey(planKey) ? BILLING_PLANS[planKey].name : undefined
}

export function formatSendCount(count: number): string {
  return Math.max(0, Math.floor(count)).toLocaleString('en-US')
}

/** "1 email send", "27,885 email sends". */
export function formatEmailSends(count: number): string {
  const whole = Math.max(0, Math.floor(count))
  return `${formatSendCount(whole)} email send${whole === 1 ? '' : 's'}`
}

/** "Oct 18": the UTC day a billing period ends, when the allowance resets. */
export function formatAllowanceResetDate(periodEnd: number): string {
  return new Date(periodEnd).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

/**
 * What a refused sender can do: upgrade, send to fewer recipients, cancel a
 * send in progress when sends are reserved, or wait for the reset.
 */
export function describeEmailSendRemedies(quota: {
  reserved?: number
  periodEnd?: number
}): string {
  const wait =
    quota.periodEnd === undefined
      ? 'wait for the next billing period'
      : `wait until your allowance resets on ${formatAllowanceResetDate(quota.periodEnd)}`
  return (quota.reserved ?? 0) > 0
    ? `Upgrade your plan, send to fewer recipients, cancel a send in progress, or ${wait}. ${RESERVED_SENDS_EXPLAINER}`
    : `Upgrade your plan, send to fewer recipients, or ${wait}.`
}

/** A reserved count missing from an older payload reads as none. */
function reservedOf(quota: EmailSendQuotaNumbers): number {
  return Math.max(quota.reserved ?? 0, 0)
}

export function remainingEmailSends(quota: EmailSendQuotaNumbers): number {
  return Math.max(quota.limit - quota.used - reservedOf(quota), 0)
}

export function classifyEmailSendQuota(
  quota: EmailSendQuotaNumbers
): EmailSendQuotaKind {
  const remaining = remainingEmailSends(quota)
  if (quota.used >= quota.limit) {
    return 'exhausted'
  }
  if (quota.requested !== undefined && quota.requested > remaining) {
    return 'shortfall'
  }
  if (remaining <= 0) {
    return 'reserved_only'
  }
  return 'available'
}

/**
 * Where the allowance went, e.g. "911 used and 27,880 reserved for sends in
 * progress". Only names reserved sends when there are any.
 */
export function describeEmailSendUsage(quota: EmailSendQuotaNumbers): string {
  const reserved = reservedOf(quota)
  return reserved > 0
    ? `${formatSendCount(quota.used)} used and ${formatSendCount(reserved)} reserved for sends in progress`
    : `${formatSendCount(quota.used)} of ${formatSendCount(quota.limit)} used`
}

/**
 * The refusal (or usage) in one or two sentences, safe to show end users and
 * API callers verbatim. `planName` reads "on the Growth plan" when given.
 */
export function describeEmailSendQuota(
  quota: EmailSendQuotaNumbers & { planName?: string }
): string {
  const onPlan = quota.planName ? ` on the ${quota.planName} plan` : ''
  const remaining = remainingEmailSends(quota)
  switch (classifyEmailSendQuota(quota)) {
    case 'exhausted':
      return `You've used all ${formatEmailSends(quota.limit)} included${onPlan} this billing period.`
    case 'shortfall':
      return `This send needs ${formatEmailSends(quota.requested ?? 0)}, but only ${formatSendCount(remaining)} ${remaining === 1 ? 'is' : 'are'} left${onPlan} this billing period (${describeEmailSendUsage(quota)}).`
    case 'reserved_only':
      return `Every email send left${onPlan} this billing period is reserved for sends in progress (${describeEmailSendUsage(quota)}). ${RESERVED_SENDS_EXPLAINER}`
    case 'available':
      return `${formatSendCount(remaining)} of ${formatEmailSends(quota.limit)} are left${onPlan} this billing period (${describeEmailSendUsage(quota)}).`
  }
}
